import React from 'react';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Icon from '@material-ui/core/Icon';
import Input from '@material-ui/core/Input';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import { Dialog } from '../../../components/dialog';
import EnhancedTable from '../../../components/table';
import ErrorMessage from '../../../components/error-message';
import { TooltipButton } from '../../../components/tooltip-button';

// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as billActions,
    selectors as billSelectors,
    groupedBillBulkActions,
    groupedBillStatus,
} from '../../../redux/reducers/bills';
import { partnerBillStatus } from '../../../redux/reducers/partner-bills';
import { tableRequestPoints } from '../../../redux/reducers/table';
import { BillingStep, BillPartner } from '../../../redux/reducers/api-types';
// Ressources
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import withGlobalStyles from '../../../utils/global-styles';
import styles from './bills-list-pending-style';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { Cell, createCell } from '../../../components/table/table-cell-creator';
import { clsx } from '../../../utils/htmlUtils';
import { FormatType, HeadCell, PickerOption } from '../../../local-types';

interface GroupedBillPendingListProps extends WithTranslation, WithStyles, RouteComponentProps {
    globalClasses: any;
    step: BillingStep;
    getGroupedStepError: ErrorPayload;
    updateGroupedBillsPendingError: ErrorPayload;
    bulkActionsGroupedBillsPendingError: ErrorPayload;
    getStep: (payload: { start_date: string; end_date: string }, callback?: Function) => void;
    updateGroupedBillStatus: (id: string, status: any, callback: React.EffectCallback) => void;
    bulkAction: (mode: string, payload: { start_date: string; end_date: string }, callback: Function) => void;
}

class GroupedBillPendingListPage extends React.Component<GroupedBillPendingListProps> {
    tableRef: any;
    dialogRef: any;
    paiementOptions: PickerOption[];

    isAdmin: boolean;
    canRectify: boolean;

    constructor(props) {
        super(props);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.RIDER_BILLING_MGMT, mode: RightRank.W });
        this.canRectify = UserUtils.checkRightsOrAdmin({ name: AccountRight.BILL_RECTIFICATION, mode: RightRank.W });

        props.getStep(this.generateDatePeriodPayload());

        const { t } = props;
        this.paiementOptions = [
            { value: '', label: '' },
            { value: 'wire_transfer', label: t('common:paiement.wire_transfer') },
            { value: 'sepa', label: t('common:paiement.sepa') },
            { value: 'card_adyen', label: t('common:paiement.card_adyen') },
        ];
    }

    //=======================================//
    //=============== Helpers ===============//
    //=======================================//

    generateDatePeriodPayload = (startDate?: Date) => {
        const start_date = startDate ? new Date(startDate) : DateUtils.getPreviousMonth(new Date());
        start_date.setHours(0);
        start_date.setDate(1);
        const nextMonth = DateUtils.getNextMonth(start_date);
        nextMonth.setHours(0);
        const payload = {
            start_date: DateUtils.dateFormat(start_date.getTime(), 'isoDateTime'),
            end_date: DateUtils.dateFormat(nextMonth.getTime(), 'isoDateTime'),
        };
        return payload;
    };

    //========================================//
    //=============== Handlers ===============//
    //========================================//

    handleStatus = (row: any, status: string) => () => {
        this.props.updateGroupedBillStatus(row.id, status, () => {
            this.tableRef.reload();
        });
    };

    /**
     * Handle the different actions considering the date given and the newStatus
     * @param startDate Start of the period -> end is auto calculated 1 week later
     * @param newStatus groupedBillStatus
     */
    handleStatuses = (startDate: Date, newStatus: string) => {
        // console.log(startDate, newStatus);
        if (startDate) {
            const payload = this.generateDatePeriodPayload(startDate);
            if (newStatus === groupedBillBulkActions.SEND_AND_PAY) {
                this.props.bulkAction(groupedBillBulkActions.SEND_AND_PAY, payload, (dispatch, response) => {
                    if (this.dialogRef) {
                        this.dialogRef.showDialog();
                    }
                });
            }
            // if (newStatus === groupedBillBulkActions.PAY) {
            //     this.props.bulkAction(groupedBillBulkActions.PAY, payload, () => {
            //         this.tableRef.reload();
            //     });
            // } else if (newStatus === groupedBillBulkActions.SENT) {
            //     this.props.bulkAction(groupedBillBulkActions.SENT, payload, () => {
            //         this.tableRef.reload();
            //     });
            // }
        }
    };

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateFilters = (): Object => {
        const filterParams: Object = {
            'in[status]': ['pending'],
        };
        return filterParams;
    };

    generateHeaders = (): HeadCell[] => {
        const { t, globalClasses, classes } = this.props;
        const statusOptions: HeadCell[] = [
            createCell(t, Cell.BILL_NUMBER),
            createCell(t, Cell.BILL_DOCUMENT_PATH),
            createCell(t, Cell.BILL_XLS_PATH),
            createCell(t, Cell.BILL_CREATED_AT),
            {
                id: 'start_date',
                label: t('facturation_period'),
                filterable: false,
                formatCell: function (row) {
                    return `${DateUtils.tsToDate(row.start_date, true)} - ${DateUtils.tsToDate(row.end_date, true)}`;
                },
            },
            createCell(t, Cell.COMPANY_ID),
            {
                id: 'status',
                label: t('common:table.status'),
                filterable: false,
                sortable: false,
                formatCell: function (row) {
                    let res = t('statusOptions.paid');
                    if (row.status === 'cancelled') {
                        res = t('statusOptions.cancelled');
                    } else if (row.status !== 'paid') {
                        res = t('statusOptions.not_paid');
                    }
                    return res;
                },
            },
            {
                id: 'remaining_balance',
                label: t('remaining_balance'),
                type: FormatType.Price,
                sortable: false,
                filterable: false,
                formatCell: function (row) {
                    let res = FormattingUtils.price(0, true);
                    if (row.status !== 'paid') {
                        const remaining =
                            row.payment_type !== 'card_adyen' ? row.remaining_balance : row.amount_with_tax;
                        res = FormattingUtils.price(remaining);
                    }
                    return res;
                },
            },
            // createCell(t, Cell.AMOUNT_PRE_TAX),
            createCell(t, Cell.AMOUNT_WITH_TAX),
            {
                id: 'payment_type',
                label: t('payment_type'),
                sortable: false,
                type: FormatType.Picker,
                pickerOptions: this.paiementOptions,
                formatCell: function (row) {
                    return t('common:paiement.' + row.payment_type);
                },
            },
        ];

        if (this.isAdmin) {
            statusOptions.push({
                id: 'action',
                label: t('common:action'),
                sortable: false,
                filterable: false,
                noLink: true,
                cellProps: {
                    className: classes.actionCell,
                },
                formatCell: (row: BillPartner) => {
                    return (
                        <div>
                            {this.canRectify && row.status !== 'paid' && (
                                <TooltipButton title={t('credit.modify')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: 8 }}
                                        href={`${paths.billsRectificationsAdd}?bill_number=${row.bill_number}&id=${row.id}`}
                                        className={globalClasses.btnAutoWidth}
                                    >
                                        <Icon fontSize="small">receipt</Icon>
                                    </Button>
                                </TooltipButton>
                            )}
                            {row.status === partnerBillStatus.PENDING && (
                                <Dialog
                                    btnTitle={t('grouped.pending.status.canceled')}
                                    description={t('partner_bill:cancel_bill_desc')}
                                    customButton={
                                        <TooltipButton title={t('grouped.pending.status.canceled')}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                                            >
                                                <Icon fontSize="small">close</Icon>
                                            </Button>
                                        </TooltipButton>
                                    }
                                    onOK={this.handleStatus(row, groupedBillStatus.CANCELED)}
                                />
                            )}
                        </div>
                    );
                },
            });
        }
        return statusOptions;
    };

    renderSelectionActions = () => {
        const { t, globalClasses, step } = this.props;
        const prevMonth = DateUtils.getPreviousMonth();
        const defaultMonth = DateUtils.dateFormat(prevMonth.getTime(), 'yyyy-mm');

        const enabled =
            // (step.step === 'create_month_bills' && step.status === 'unlaunched') ||
            // (step.step === 'create_month_bills_docs' && step.status === 'done') ||
            (step.step === 'send_month_bills' && step.status === 'unlaunched') || step.step === 'finished';

        const actionButton = (
            <Button
                startIcon={<Icon>mail</Icon>}
                size="small"
                variant="contained"
                disabled={!enabled}
                className={globalClasses.btnSuccess}
            >
                {t('grouped.pending.bulk_send_and_pay')}
            </Button>
        );

        return (
            this.isAdmin && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <div style={{ marginRight: 20 }}>{`${t('partner_bill:bulk_action')} ${
                        !enabled ? t('step_detail', step.step) : ''
                    }`}</div>
                    {enabled ? (
                        <Dialog
                            btnTitle={t('grouped.pending.bulk_send_and_pay')}
                            description={t('grouped.pending.bulk_send_and_pay_desc')}
                            customButton={
                                <TooltipButton title={t('grouped.pending.bulk_send_and_pay_desc')}>
                                    {actionButton}
                                </TooltipButton>
                            }
                            formComponent={
                                <>
                                    <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                        <div>{t('grouped.pending.period_choice')}</div>
                                        <Input
                                            // type="date"
                                            type="month"
                                            name={'start_date'}
                                            required={true}
                                            defaultValue={defaultMonth}
                                        />
                                    </FormControl>
                                </>
                            }
                            onOK={(fields) =>
                                this.handleStatuses(fields.start_date, groupedBillBulkActions.SEND_AND_PAY)
                            }
                        />
                    ) : (
                        actionButton
                    )}
                    {/* <Dialog
                        btnTitle={t('grouped.pending.bulk_send')}
                        description={t('partner_bill:bulk_validate_desc')}
                        customButton={
                            <TooltipButton title={t('partner_bill:bulk_validate_desc')}>
                                <Button
                                    startIcon={<Icon>mail</Icon>}
                                    size="small"
                                    variant="contained"
                                    className={globalClasses.btnSuccess}
                                >
                                    {t('grouped.pending.bulk_send')}
                                </Button>
                            </TooltipButton>
                        }
                        formComponent={
                            <>
                                <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                    <div>{t('grouped.pending.period_choice')}</div>
                                    <Input
                                        type="date"
                                        name={'start_date'}
                                        required={true}
                                        defaultValue={defaultMonth}
                                    />
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleStatuses(fields.start_date, groupedBillBulkActions.SEND)}
                    />
                    <div style={{ marginLeft: 20 }} />
                    <Dialog
                        btnTitle={t('grouped.pending.bulk_pay')}
                        description={t('partner_bill:bulk_mark_as_paid_desc')}
                        customButton={
                            <TooltipButton title={t('partner_bill:bulk_mark_as_paid_desc')}>
                                <Button startIcon={<Icon>euro</Icon>} size="small" variant="contained" color="primary">
                                    {t('grouped.pending.bulk_pay')}
                                </Button>
                            </TooltipButton>
                        }
                        formComponent={
                            <>
                                <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                    <div>{t('grouped.pending.period_choice')}</div>
                                    <Input
                                        type="date"
                                        name={'start_date'}
                                        required={true}
                                        defaultValue={defaultMonth}
                                    />
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleStatuses(fields.start_date, groupedBillBulkActions.PAY)}
                    /> */}
                </div>
            )
        );
    };

    render() {
        const { t, getGroupedStepError, updateGroupedBillsPendingError, bulkActionsGroupedBillsPendingError } =
            this.props;

        return (
            <App
                needRights={AccountRight.RIDER_BILLING_MGMT}
                disableBreadcrumb
                header={{
                    title: t('title_grouped_pending'),
                }}
            >
                <ErrorMessage payload={getGroupedStepError} />
                <ErrorMessage payload={updateGroupedBillsPendingError} />
                <ErrorMessage payload={bulkActionsGroupedBillsPendingError} />
                {this.renderSelectionActions()}
                <Dialog
                    description={t('grouped.pending.bulk_send_and_pay_response')}
                    onRef={(ref) => (this.dialogRef = ref)}
                    disableEscapeManual={true}
                    onOK={() => {
                        this.props.getStep(this.generateDatePeriodPayload());
                    }}
                    noKOButton={true}
                />
                <EnhancedTable
                    onRef={(ref) => (this.tableRef = ref)}
                    headers={this.generateHeaders()}
                    type={tableRequestPoints.BILLS_GROUPED_PENDING_BETA}
                    filterParams={this.generateFilters()}
                    filterVisible={false}
                />
            </App>
        );
    }
}

const GroupedBillPendingListPageWithTranslation = withGlobalStyles(
    withStyles(styles)(withTranslation(['bill', 'partner_bill', 'common'])(GroupedBillPendingListPage)),
);

const makemapStateToProps = () => {
    const mapStateToProps = (state) => {
        return {
            step: billSelectors.getStep(state),
            getGroupedStepError: errorSelectors.getGroupedStepError(state),
            updateGroupedBillsPendingError: errorSelectors.updateGroupedBillsPendingError(state),
            bulkActionsGroupedBillsPendingError: errorSelectors.bulkActionsGroupedBillsPendingError(state),
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = {
    getStep: billActions.getStep,
    bulkAction: billActions.bulkAction,
    updateGroupedBillStatus: billActions.updateGroupedBillStatus,
};

export default connect(makemapStateToProps, mapDispatchToProps)(GroupedBillPendingListPageWithTranslation);
